import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../generic-components/Heading';
import TransferType from './TransferType';
import { ReactComponent as ExpressTypeIcon } from '../../../assets/svg/Express.svg';
import { ReactComponent as StandardTypeIcon } from '../../../assets/svg/Standard.svg';
import TransferFee from './TransferFee';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { useAchOutFeesQuery } from '../../../hooks/query/useAchOutFeesQuery';
import { useStore } from '../../../store';
import { IFeeItem } from '../../../types';
import { IFeeData } from '../../../store/createBankTransferSlice';

const STYLES = {
  WRAPPER: 'flex flex-col border border-bw03 rounded-[8px]',
  MAIN: 'flex flex-col pt-[12px] px-[16px]',
  SECTION_TITLE: 'font-metricR text-[16px] text-bw08 mb-[8px]',
};

const TRANSFER_TYPES = {
  EXPRESS: 'EXPRESS',
  STANDARD: 'STANDARD',
};

interface ITransferTypesPickerProps {
  transferAmount: string;
  setChosenFeeData: (feeData: IFeeData) => void;
}

const TransferTypesPicker = ({ transferAmount, setChosenFeeData }: ITransferTypesPickerProps) => {
  const { user: { customerId }, getPersonalAccount } = useStore(state => state);
  const { accountExternalId } = getPersonalAccount();
  const [transferType, setTransferType] = useState<string>('');
  const { t } = useTranslation();
  const { refetch: getAchOutFees, data: achFeesResponse, isLoading: isFeesLoading } = useAchOutFeesQuery(
    {
      customerId,
      accountExternalId,
      amount: transferAmount,
      onSuccess: () => {},
    },
  );
  const expressTransferData = useMemo(() => {
    const { fees } = achFeesResponse || {};
    const data = fees?.find((feeItem: IFeeItem) => feeItem?.achTransferType === TRANSFER_TYPES.EXPRESS) || null;
    return data;
  }, [achFeesResponse]);
  const standardTransferData = useMemo(() => {
    const { fees } = achFeesResponse || {};
    const data: IFeeItem | null = fees?.find((feeItem: IFeeItem) => feeItem?.achTransferType === TRANSFER_TYPES.STANDARD) || null;
    return data;
  }, [achFeesResponse]);
  const transferFee = useMemo(() => {
    if (transferType === TRANSFER_TYPES.STANDARD && standardTransferData) {
      setTimeout(() => {
        setChosenFeeData(standardTransferData);
      }, 0);
      return t('SEND_MONEY.REVIEW.FREE_FEE');
    }
    if (transferType === TRANSFER_TYPES.EXPRESS && expressTransferData) {
      setTimeout(() => {
        setChosenFeeData(expressTransferData);
      }, 0);
      return convertValueToUSD(expressTransferData.fee, 2);
    }
    return t('SEND_MONEY.REVIEW.NO_FEE');
  }, [transferType]);
  const feeDescription = useMemo(() => achFeesResponse?.feeDescription, [achFeesResponse]);
  useEffect(() => {
    getAchOutFees();
  }, []);
  return (
    <div className={STYLES.WRAPPER}>
      <div className={STYLES.MAIN}>
        <Heading title={t('SEND_MONEY.REVIEW.TRANSFER_TYPES_SECTION_TITLE')} className={STYLES.SECTION_TITLE} />
        <TransferType
          type={TRANSFER_TYPES.EXPRESS}
          isLoading={isFeesLoading}
          title={t('SEND_MONEY.REVIEW.TRANSFER_TYPES_TITLE.EXPRESS')}
          onClick={() => setTransferType(TRANSFER_TYPES.EXPRESS)}
          isSelected={transferType === TRANSFER_TYPES.EXPRESS}
          arrivalDate={expressTransferData?.arrivalDate}
          SVGIcon={ExpressTypeIcon}
        />
        <TransferType
          type={TRANSFER_TYPES.STANDARD}
          isLoading={isFeesLoading}
          title={t('SEND_MONEY.REVIEW.TRANSFER_TYPES_TITLE.STANDARD')}
          onClick={() => setTransferType(TRANSFER_TYPES.STANDARD)}
          isSelected={transferType === TRANSFER_TYPES.STANDARD}
          arrivalDate={standardTransferData?.arrivalDate}
          SVGIcon={StandardTypeIcon}
        />
      </div>
      <TransferFee transferFee={transferFee} feeDescription={feeDescription} />
    </div>
  );
};

export default TransferTypesPicker;
